import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import CSSModules from 'react-css-modules';
import { autobind } from 'core-decorators';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import * as FormatUtils from 'utils/formatUtils';
import {contactUsLink} from '../auth/staticComponents'
import { change, Field, reduxForm, formValueSelector } from 'redux-form';
import RadioGroup from 'components/common/material-form/RadioGroup';
import RadioButton from 'components/common/material/RadioButton';
import Dialog from 'components/common/material/Dialog';
import RaisedButton from 'components/common/material/RaisedButton';
import * as sunbitConstants from 'constants/sunbit';
import * as constants from './constants';
import * as purchaseConstants from '../purchases/constants';
import {getPurchaseMergedStatus} from '../purchases/helpers';
import PaymentAmount from './PaymentAmount';
import PayNowSuccess from './PayNowSuccess';
import PayNowConsent from './PayNowConsent';
import LoadingBlocking from 'components/loading/LoadingBlocking';
import Payments from 'components/payments/Payments';
import * as thunkActionHelpers from 'utils/redux/thunkActionHelpers';
import DebitCardForm from '../components/common/debitCardForm/DebitCardForm';
import _get from "utils/get";
import getCurrentPaymentMethodTextByPurchase from "utils/getCurrentPaymentMethodTextByPurchase";
import { payNowAction, simulateUnscheduledPaymentAction, simulateUnscheduledPaymentPendingAction, payNowResetAction, payNowResetFailureAction } from './redux';
import { datadogRum } from '@datadog/browser-rum';
import { purchaseUpdateDebitCardAction } from '../purchases/redux';
import styles from './payNow.scss';

function validate(values, props) {
  let errors = {};
  const { purchase: { leftAmountToPay }, intl: { formatMessage } } = props;
  const amount = Number(values.amount);
  const amountOption = values.amountOption;

  if (leftAmountToPay <= 0) {
    errors.amountOption = true;
  }

  if ([constants.PAY_NOW_AMOUNT_OPTION_PARTIAL, constants.PAY_NOW_AMOUNT_OPTION_NEXT].includes(amountOption)) {
    if (amount > leftAmountToPay) {
      errors.amount = formatMessage({ id: 'payNow.errors.amount.exceed' }, { maximum: FormatUtils.addPriceSymbol(leftAmountToPay) });
      errors.amountOption = true;
    }
  }

  return errors;
}

@injectIntl
@reduxForm({
  validate,
  initialValues: {
    amount: '',
    amountOption: constants.PAY_NOW_AMOUNT_OPTION_PARTIAL,
    nextPaymentsToPay: 0,
    nextPaymentsToPayDates: [],
    applyAmountToNextPayments: true,
  }
})
@connect(
  (state, props) => {
    const selector = formValueSelector(props.form);

    return {
      type: state.payNow.type,
      status: state.payNow.status,
      simulatedPayments: state.payNow.simulatedPayments,
      amount: selector(state, 'amount'),
      amountOption: selector(state, 'amountOption'),
      nextPaymentsToPay: selector(state, 'nextPaymentsToPay'),
      nextPaymentsToPayDates: selector(state, 'nextPaymentsToPayDates'),
      applyAmountToNextPayments: selector(state, 'applyAmountToNextPayments'),
      errors: selector(state, 'syncErrors'),
      useCurrentPayentMethod: selector(state, 'useCurrentPayentMethod'),
    };
  },
  (dispatch, props) => {
    return {
      payNowAction: (externalId, amount, applyAmountToNextPayments, paymentMethod) => {return dispatch(payNowAction(externalId, amount, applyAmountToNextPayments, paymentMethod))},
      purchaseUpdateDebitCardAction: (externalId, debitCardDetails) => {return dispatch(purchaseUpdateDebitCardAction(externalId, debitCardDetails.cardNumber, debitCardDetails.expirationMonth, debitCardDetails.expirationYear, debitCardDetails.cvv))},
      simulateUnscheduledPaymentAction: (externalId, amount, applyAmountToNextPayments) => dispatch(simulateUnscheduledPaymentAction(externalId, amount, applyAmountToNextPayments)),
      simulateUnscheduledPaymentPendingAction: () => dispatch(simulateUnscheduledPaymentPendingAction()),
      payNowResetAction: () => dispatch(payNowResetAction()),
      payNowResetFailureAction: () => dispatch(payNowResetFailureAction()),
      changeAmountAction: (amount) => dispatch(change(props.form, 'amount', amount)),
      changeNextPaymentsToPayAction: (nextPaymentsToPay, nextPaymentsToPayDates) => {
        dispatch(change(props.form, 'nextPaymentsToPay', nextPaymentsToPay));
        dispatch(change(props.form, 'nextPaymentsToPayDates', nextPaymentsToPayDates));
      },
      changeAmountOptionAction: (amountOption) => dispatch(change(props.form, 'amountOption', amountOption)),
      setUseCurrentPayentMethodAction: (isCurrent) => dispatch(change(props.form, 'useCurrentPayentMethod', isCurrent))
    };
  }
)
@CSSModules(styles, { allowMultiple: true })
class PayNowForm extends React.Component {

  static propTypes = {
    intl: intlShape, // react-inlt
    type: PropTypes.string, //payNow reducer
    status: PropTypes.string, //payNow reducer
    simulatedPayments: PropTypes.array,
    payNowAction: PropTypes.func.isRequired, //props
    purchaseUpdateDebitCardAction: PropTypes.func.isRequired, //props
    simulateUnscheduledPaymentAction: PropTypes.func.isRequired, //props
    simulateUnscheduledPaymentPendingAction: PropTypes.func.isRequired, //props
    payNowResetAction: PropTypes.func.isRequired, //props
    payNowResetFailureAction: PropTypes.func.isRequired, //props
    changeAmountAction: PropTypes.func.isRequired, //props
    changeNextPaymentsToPayAction: PropTypes.func.isRequired, //props
    changeAmountOptionAction: PropTypes.func.isRequired, //props
    closeButtonClick: PropTypes.func.isRequired, //props
    doneCallback: PropTypes.func.isRequired, //props
    returnCallback: PropTypes.func.isRequired, //props
    returnCallbackAlternative: PropTypes.func, //props
    purchase: PropTypes.shape({ //props
      leftAmountToPay: PropTypes.number.isRequired,
      externalId: PropTypes.string.isRequired,
      cardLast4Digits: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      allPaymentsIncludingDownPayment: PropTypes.array.isRequired,
      isZeroAprPromoted: PropTypes.bool,
      zeroAprPromotionExpirationDate: PropTypes.string,
    }).isRequired,

    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired, //redux-forms
    amountOption: PropTypes.string.isRequired, //redux-forms
    useCurrentPayentMethod: PropTypes.string, //redux-forms
    nextPaymentsToPay: PropTypes.number.isRequired, //redux-forms
    nextPaymentsToPayDates: PropTypes.array.isRequired, //redux-forms
    applyAmountToNextPayments: PropTypes.bool.isRequired, //redux-forms
    invalid: PropTypes.bool.isRequired, //redux-forms
  };

  constructor(props) {
    super(props);
    this.state = {
      debitCardDetails: null,
      showConfirmPaymentDialog: false,
      showNotPayableDialog: false,
      step: constants.PAY_NOW_STEPS[0]
    };
    this.initUseCurrentPaymentMethod();
    this.amountChangeDebounceTimeout = null;
  }

  initUseCurrentPaymentMethod () {
    this.props.setUseCurrentPayentMethodAction(this.isNotDebitCardPurchase() ? "false" : "true")
  }

  componentDidMount() {
    this.props.payNowResetAction();

    const { purchase: { externalId, customerId }} = this.props;
    datadogRum.addUserAction('Start make a payment purchase flow', {
      level : 'Ok',
      extraData: {purchaseId: externalId},
      customerId
    })
  }

  componentWillReceiveProps(nextProps) {
    if (this.isSuccess(nextProps)) {
      this.props.doneCallback();

      if (nextProps.amountOption === constants.PAY_NOW_AMOUNT_OPTION_ENTIRE && this.props.returnCallbackAlternative) {
        this.props.returnCallbackAlternative();
      }
      else {
        this.props.returnCallback();
      }
    }
    else if (this.isFailed(nextProps) || this.isSimulatePaymentFailed(nextProps)) {
      this.openNotPayableDialog();
      this.closeConfirmPaymentDialog();
    }

    if (nextProps.amountOption === constants.PAY_NOW_AMOUNT_OPTION_PARTIAL && this.props.amountOption !== constants.PAY_NOW_AMOUNT_OPTION_PARTIAL) {
      this.props.payNowResetAction();
      this.props.changeAmountAction('');
      this.props.changeNextPaymentsToPayAction(0, []);
    }
    else if (nextProps.amountOption === constants.PAY_NOW_AMOUNT_OPTION_NEXT && this.props.amountOption !== constants.PAY_NOW_AMOUNT_OPTION_NEXT) {
      this.props.payNowResetAction();
      this.props.changeAmountAction(0);
      this.props.changeNextPaymentsToPayAction(0, []);
    }
    else if (nextProps.amountOption === constants.PAY_NOW_AMOUNT_OPTION_ENTIRE && this.props.amountOption !== constants.PAY_NOW_AMOUNT_OPTION_ENTIRE) {
      this.props.payNowResetAction();
      this.props.changeAmountAction(nextProps.purchase.leftAmountToPay);
      this.props.changeNextPaymentsToPayAction(0, []);
    } else if (nextProps.amountOption === constants.PAY_NOW_AMOUNT_OPTION_PAST_DUE && this.props.amountOption !== constants.PAY_NOW_AMOUNT_OPTION_PAST_DUE) {
      this.props.payNowResetAction();
      this.props.changeAmountAction(nextProps.purchase.pastDueAmount);
      this.props.changeNextPaymentsToPayAction(0, []);
    }

    if (nextProps.useCurrentPayentMethod !== this.props.useCurrentPayentMethod) {
      this.handleOtherDebitCardDetails(null)
    }
  }

  componentDidUpdate(prevProps) {
    if ([constants.PAY_NOW_AMOUNT_OPTION_PARTIAL, constants.PAY_NOW_AMOUNT_OPTION_NEXT].indexOf(this.props.amountOption) >= 0) {
      if (this.props.amount &&
        this.props.amount !== prevProps.amount ||
        this.props.applyAmountToNextPayments !== prevProps.applyAmountToNextPayments
      ) {
        this.onAmountChange();
      }
    }
  }

  @autobind
  toggleConfirmPaymentDialog() {
    this.setState({ showConfirmPaymentDialog: !this.state.showConfirmPaymentDialog });
  }

  @autobind
  closeConfirmPaymentDialog() {
    this.setState({ showConfirmPaymentDialog: false });
  }

  @autobind
  toggleNotPayableDialog() {
    this.props.payNowResetFailureAction();
    this.setState({ showNotPayableDialog: !this.state.showNotPayableDialog });
  }

  @autobind
  openNotPayableDialog() {
    this.setState({ showNotPayableDialog: true });
  }

  @autobind
  onAmountChange() {
    clearTimeout(this.amountChangeDebounceTimeout);
    this.amountChangeDebounceTimeout = setTimeout(() => {
      const { purchase: { externalId }, amount, applyAmountToNextPayments, invalid, simulateUnscheduledPaymentAction } = this.props;
      !invalid && this.props.simulateUnscheduledPaymentPendingAction();
      setTimeout(() => {
        if (!invalid) {
          simulateUnscheduledPaymentAction(externalId, amount, applyAmountToNextPayments);
        }
      }, 1000);
    }, 300);
  }

  isPending(props) {
    return thunkActionHelpers.actionPending(constants.PAY_NOW_ACTION_PURCHASE, props.type, props.status) ;
  }

  isSuccess(props) {
    return thunkActionHelpers.actionSuccess(constants.PAY_NOW_ACTION_PURCHASE, props.type, props.status);
  }

  isFailed(props) {
    return thunkActionHelpers.actionFail(constants.PAY_NOW_ACTION_PURCHASE, props.type, props.status);
  }

  isSimulatePaymentPending(props) {
    return thunkActionHelpers.actionPending(constants.PAY_NOW_ACTION_SIMULATE_UNSCHEDULED_PAYMENT, props.type, props.status);
  }

  isSimulatePaymentSuccess(props) {
    return thunkActionHelpers.actionSuccess(constants.PAY_NOW_ACTION_SIMULATE_UNSCHEDULED_PAYMENT, props.type, props.status);
  }

  isSimulatePaymentFailed(props) {
    return thunkActionHelpers.actionFail(constants.PAY_NOW_ACTION_SIMULATE_UNSCHEDULED_PAYMENT, props.type, props.status);
  }

  @autobind
  handleOtherDebitCardDetails(debitCardDetails) {
    this.setState(() => ({debitCardDetails}));
  }

  @autobind
  isNotDebitCardPurchase() {
    const {purchase: {paymentPlanPaymentMethod, updateDebitCard}} = this.props;
    return !updateDebitCard && paymentPlanPaymentMethod !== 'DEBIT';
    // TODO: above - new and simple implementation. below: the previous implementation (by marcelo at jan 17, 2018). try to understand why he chose this way.
    // const {purchase: {payments}} = this.props;
    // for (const payment of payments) {
    //   if (payment.paymentMethodType !== 'DEBIT' && payment.status !== 'PAID') {
    //     return true;
    //   }
    // }
    // return false;
  }

  @autobind
  renderPayNowButton(field) {
    const { intl: { formatMessage } } = this.props;

    const onClick = () => {
      this.toggleConfirmPaymentDialog();
    };

    return <RaisedButton dataAt="confirmBtn" primary={true} label={formatMessage({ id: 'common.confirm' })} disabled={field.disabled} onClick={onClick} />;
  }

  @autobind
  getPaymentMethodText(useNewDebitCard, newDebitCardDetails) {
    let last4Digits = useNewDebitCard ? newDebitCardDetails.cardNumber.substr(-4) : this.props.purchase.cardLast4Digits;
    return <span data-at="confirmMethodTxt"> <FormattedMessage id="payment.method.debit" /> ({last4Digits})</span>
  }

  renderConfirmDialog() {
    const { purchase: { externalId, cardLast4Digits, customerId }, amount, amountOption, applyAmountToNextPayments, payNowAction, purchaseUpdateDebitCardAction, intl: { formatMessage } } = this.props;

    const isLate = getPurchaseMergedStatus(this.props.purchase) === purchaseConstants.PURCHASE_STATUS_MERGED_LATE;
    const useNewDebitCard = this.props.useCurrentPayentMethod === "false"
    const isDefaultPayment = useNewDebitCard && _get(this.state, ['debitCardDetails', 'isDefaultPayment'])
    const dueDate = Date.now();

    const onMakePaymentClick = () => {
      const basePaymentLogData = {
        event: {
          extraData: {purchaseId: externalId},
        },
        customerId: customerId
      };

      payNowAction(externalId, amount, applyAmountToNextPayments, useNewDebitCard && this.state.debitCardDetails)
        .then((res)=> {
          if (res && !res.error) {
            datadogRum.addUserAction('Make a payment purchase success', {...basePaymentLogData, level: 'Ok'});
            if (isDefaultPayment) {
              purchaseUpdateDebitCardAction(externalId, _get(this.state, ['debitCardDetails', 'debitCardDetails']));
            }
          }
          if (res && res.error) {
            const error = _get(res, ["error", "response", "data"], null);
            datadogRum.addUserAction('Make a payment purchase failed', {...basePaymentLogData, error});
          }
        });
    };

    const actions = [
      { onClick: this.toggleConfirmPaymentDialog, label: formatMessage({ id: 'common.cancel' }) },
      { onClick: onMakePaymentClick, label: formatMessage({ id: 'common.agreeAndPay' }), primary: true },
    ];

    const dialogProps = {
      actions,
      title: formatMessage({ id: 'payNow.confirm.title' }),
      active: true,
      theme: {
        title: styles.payNowFormConfirmDialogTitle,
        body: styles.payNowFormConfirmDialogBody
      }
    };

    return (
      <div styleName="payNowFormConfirmDialogWrap">
        <Dialog {...dialogProps}>
          {this.isPending(this.props) && <LoadingBlocking />}
          <div styleName="payNowFormConfirmDialog" data-at="paymentConfirm">
            <ul>
              <li styleName="text"><span styleName="dialogTextLabel"><FormattedMessage id="payNow.confirm.amount" /> </span><span data-at="confirmAmountTxt" > {FormatUtils.addPriceSymbol(amount)}</span></li>
              <li styleName="text"><span styleName="dialogTextLabel"><FormattedMessage id="payNow.confirm.date" /> </span> <span data-at="confirmDateTxt" > {FormatUtils.transformDate(dueDate)}</span></li>
              <li styleName="text"><span styleName="dialogTextLabel"><FormattedMessage id="payNow.confirm.method" /> </span> {this.getPaymentMethodText(useNewDebitCard, _get(this.state, ['debitCardDetails', 'debitCardDetails']))}</li>
            </ul>
            <PayNowConsent dataAt="PayNowConsentTxt" isLate={isLate} isDefaultPayment={isDefaultPayment} amountOption={amountOption} cardNumber={cardLast4Digits} amount={amount} externalId={externalId} dueDate={dueDate} />
          </div>
        </Dialog>
      </div>
    );
  }

  renderNotPayableDialog() {

    const { intl: { formatMessage } } = this.props;

    const actions = [
      { onClick: this.toggleNotPayableDialog, label: formatMessage({ id: 'common.close' }) }
    ];

    return (
      <Dialog active={this.state.showNotPayableDialog} actions={actions}>
        <FormattedMessage id="purchase.notPayable.notice" values={{ phoneNumber: sunbitConstants.PHONE_NUMBER }} />
      </Dialog>
    );
  }

  @autobind
  renderPaymentDetails() {
    const { amount, purchase, amountOption, simulatedPayments, nextPaymentsToPay, applyAmountToNextPayments, changeAmountAction, changeNextPaymentsToPayAction, changeAmountOptionAction, intl: { formatMessage }} = this.props;

    return <div styleName="innerFormContainer">
      <div styleName="partition">
        <PaymentAmount purchase={purchase}
          amountOption={amountOption}
          amount={amount}
          nextPaymentsToPay={nextPaymentsToPay}
          applyAmountToNextPayments={applyAmountToNextPayments}
          changeAmountAction={changeAmountAction}
          onAmountChange={this.onAmountChange}
          changeNextPaymentsToPayAction={changeNextPaymentsToPayAction}
          changeAmountOptionAction={changeAmountOptionAction}
        />
      </div>
      {
        this.isSimulatePaymentPending(this.props) &&
        <div styleName="partition">
          <LoadingBlocking />
          Calculating your new payment plan...
        </div>
      }
      {
        !this.props.invalid && simulatedPayments &&
        <div styleName={classNames('partition', 'simulate')}>
          <div styleName="scheduleInfo"><FormattedMessage id="payNow.schedule.info" /></div>
          <Payments data={simulatedPayments} />
        </div>
      }
      <div styleName="partition last">
        <div styleName="actions">
          <RaisedButton dataAt="cancelBtn" label={formatMessage({ id: 'common.cancel' })} onClick={this.props.closeButtonClick} />
          <RaisedButton dataAt="nextBtn" primary={true} label={formatMessage({ id: 'common.next' })} disabled={!this.props.amount || this.props.invalid || (!this.isSimulatePaymentSuccess(this.props) && this.props.amountOption === constants.PAY_NOW_AMOUNT_OPTION_PARTIAL)} onClick={()=>this.setState({step: constants.PAY_NOW_STEPS[1]})} />
        </div>
      </div>

    </div>
  }

  @autobind
  isSubmitable () {
    if (this.props.invalid) {
      return false;
    }

    if (this.props.useCurrentPayentMethod === "false") {
      return !!_get(this.state, ['debitCardDetails', 'debitCardDetails'])
    }

    return !this.isNotDebitCardPurchase()
  }

  @autobind
  renderPaymentMethodDetails() {
    const { intl: { formatMessage } } = this.props;
    const currentPaymentMethodLabel = <div>
      {formatMessage({ id: 'payNow.paymentMethod.current' }, { paymentMethod: getCurrentPaymentMethodTextByPurchase(this.props.purchase) })}
      {/* {this.isNotDebitCardPurchase() && <IconButtonTooltip tooltipShowOnClick={true}
        icon="info_outline"
        tooltip={formatMessage({id: 'payNow.paymentMethod.onlyDebitInfo'})}
      />} */}
    </div>

    return <div styleName="innerFormContainer">
      <h2 styleName="heading" data-at="payment-method-title">{formatMessage({ id: 'payNow.paymentMethod.Title' })}</h2>
      <div styleName="partition payment-method-form">
        <Field component={RadioGroup} name="useCurrentPayentMethod" className={styles.amountOptions}>
            <RadioButton dataAt="use-current-payment-method-true" styleName="option" wrapClassName={styles.optionWrap} disabled={this.isNotDebitCardPurchase()} value={"true"} label={currentPaymentMethodLabel} />
            {this.isNotDebitCardPurchase() && <div styleName="errorContainer">
              <FormattedMessage id="payNow.paymentMethod.onlyDebitInfo" values={{contactUsLink}}/>
            </div>}
            <RadioButton dataAt="use-current-payment-method-false" styleName="option" value={"false"} label={formatMessage({ id: 'payNow.paymentMethod.other' })} />
        </Field>
      </div>
      {this.props.useCurrentPayentMethod === "false" && <div styleName="partition">
        <DebitCardForm onChange={this.handleOtherDebitCardDetails} name="debitCardDetails" showSetDefaultPaymentMethodCheckbox={true} />
      </div>}
      <div styleName="partition last">
        <div styleName="actions">
          <RaisedButton dataAt="cancelBtn" label={formatMessage({ id: 'common.back' })} onClick={()=>this.setState({step: constants.PAY_NOW_STEPS[0]})} />
          <Field name="phase" component={this.renderPayNowButton} disabled={!this.isSubmitable()} />
        </div>
      </div>
    </div>
  }

  render() {
    return (
      <form styleName="payNowForm" autoComplete={'off'} onSubmit={() => false}>
        {this.state.step === constants.PAY_NOW_STEPS[0] && this.renderPaymentDetails()}
        {this.state.step === constants.PAY_NOW_STEPS[1] && this.renderPaymentMethodDetails()}
        {
          this.isSuccess(this.props) &&
          <div styleName="partition">
            <PayNowSuccess />
          </div>
        }
        {this.state.showConfirmPaymentDialog && this.renderConfirmDialog()}
        {this.state.showNotPayableDialog && this.renderNotPayableDialog()}
      </form>
    );
  }
}

export default PayNowForm;



// WEBPACK FOOTER //
// ./src/pay-now/PayNowForm.js